<script lang="ts">
  import { get } from 'svelte/store';
  import { feed, loadFeed, loadMore } from '../lib/stores/feed';
  import { filters } from '../lib/stores/filters';
  import { derive, clientVisible, runAction } from '../lib/stores/triage';
  import { nextIndex, type NavKey } from '../lib/keyboard';
  import type { ShowMode } from '../lib/types';
  import Tile from './Tile.svelte';

  let {
    lightboxOpen = false,
    onOpen,
  }: { lightboxOpen?: boolean; onOpen?: (id: number) => void } = $props();

  let gridEl: HTMLDivElement | undefined = $state();
  let sentinelEl: HTMLDivElement | undefined = $state();

  const SUB: Record<ShowMode, string> = {
    unseen: 'chưa xem · mới nhất trước',
    all: 'tất cả · mới nhất trước',
    saved: 'đã lưu · mới nhất trước',
  };

  const visible = $derived(
    $feed.items.filter((listing) => clientVisible(derive(listing), $filters.show)),
  );

  let selectedIndex = $state(-1);

  const NAV_KEYS: NavKey[] = ['j', 'k', 'ArrowDown', 'ArrowUp'];

  // $derived only propagates when the extracted value itself changes, so this
  // stays stable across triage patches (save/hide/view) that touch other
  // fields on the feed store without bumping reloadToken.
  const reloadToken = $derived($feed.reloadToken);

  // A fresh (re)load resets the keyboard cursor.
  $effect(() => {
    reloadToken;
    selectedIndex = -1;
  });

  // Keep the cursor inside the visible range after an action shrinks the list.
  $effect(() => {
    if (selectedIndex >= visible.length) selectedIndex = visible.length - 1;
  });

  function perRow(): number {
    const tiles = gridEl?.querySelectorAll<HTMLElement>('.tile');
    if (!tiles || tiles.length === 0) return 1;
    const firstTop = tiles[0]!.offsetTop;
    let count = 0;
    for (const tile of tiles) {
      if (tile.offsetTop === firstTop) count += 1;
      else break;
    }
    return count || 1;
  }

  function onKeydown(event: KeyboardEvent): void {
    if (lightboxOpen) return;
    const target = event.target as HTMLElement | null;
    if (target?.matches?.('input, textarea, select')) return;

    const items = visible;
    if (items.length === 0) return;

    if (NAV_KEYS.includes(event.key as NavKey)) {
      event.preventDefault();
      selectedIndex = nextIndex(selectedIndex, event.key as NavKey, items.length, perRow());
      gridEl
        ?.querySelectorAll<HTMLElement>('.tile')
        [selectedIndex]?.scrollIntoView({ block: 'nearest' });
      return;
    }

    if (selectedIndex < 0) return;
    const listing = items[selectedIndex];
    if (!listing) return;

    if (event.key === 'Enter') {
      void runAction(listing, 'view');
      onOpen?.(listing.id);
    } else if (event.key === 's') {
      void runAction(listing, 'save');
    } else if (event.key === 'x') {
      void runAction(listing, 'hide');
    } else if (event.key === 'o') {
      void runAction(listing, 'open');
    }
  }

  // Infinite scroll: page in more while a cursor remains and we are not mid-load.
  $effect(() => {
    if (!sentinelEl) return;
    const io = new IntersectionObserver((entries) => {
      const state = get(feed);
      if (entries.some((e) => e.isIntersecting) && state.cursor !== '' && !state.loading) {
        void loadMore(get(filters));
      }
    });
    io.observe(sentinelEl);
    return () => io.disconnect();
  });

  function retry(): void {
    void loadFeed(get(filters));
  }
</script>

<svelte:window onkeydown={onKeydown} />

<section class="feed" aria-label="Bảng tin">
  <div class="feedhead">
    <b>{visible.length}</b>
    <span>{SUB[$filters.show]}</span>
  </div>

  {#if $feed.loading && $feed.items.length === 0}
    <p class="feedmsg">Đang tải…</p>
  {:else if $feed.error}
    <p class="feedmsg err">
      Không tải được bảng tin: {$feed.error}
      <button type="button" onclick={retry}>thử lại</button>
    </p>
  {:else if visible.length === 0}
    <p class="feedmsg">Không có listing nào khớp bộ lọc hiện tại.</p>
  {:else}
    <div class="grid" bind:this={gridEl}>
      {#each visible as listing, index (listing.id)}
        <Tile
          {listing}
          selected={index === selectedIndex}
          onAct={(action) => {
            void runAction(listing, action);
          }}
          onOpen={() => {
            void runAction(listing, 'view');
            onOpen?.(listing.id);
          }}
        />
      {/each}
    </div>
    <div class="sentinel" bind:this={sentinelEl}></div>
  {/if}
</section>
